import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import '../../Styles/ArtistRoot.css';
import '../../Styles/Admin.css';

import COUNTRIES from '../../utils/Countries';
import GENRES from '../../utils/Genres';

import { BsFileEarmarkImage, BsFileEarmarkMusic } from 'react-icons/bs';
import { MdCheckCircle, MdInfoOutline } from 'react-icons/md';
import { Formik } from 'formik';
import * as Yup from 'yup';
import izenuAPI from '../../services/izenuAPI';
import UpcQuestion, { upcProblem, upcPayload } from './UpcQuestion';
import RightsInputs, { EMPTY_RIGHTS, appendRights } from './RightsInputs';
import CreditsInputs from './CreditsInputs';
import { Check, useImageSize } from '../adminRoots/components/AdSpecHint';
import { AUDIO_ACCEPT, AUDIO_FORMATS_HINT, AUDIO_QUALITY_HINT, audioExt, audioFileProblem, uploadTrackAudio } from '../../utils/audioUpload';

// The audio goes straight to S3 (utils/audioUpload.js); only the cover travels
// with the insert request, under PHP's 40 MB.
const COVER_IDEAL_PX = 3000;
const SONG_IDEAL_KBPS = 320;
const COVER_MAX_MB = 5;

const INITIAL_VALUES = {
    title: '',
    tags: '',
    rights: EMPTY_RIGHTS,
    credits: [],
    genre: '',
    country: 'Angola',
    link: '',
    isrc: '',
    // 'existing' = already out on other platforms, bring its ISRC;
    // 'izenu' = Izenu assigns one. Left unanswered on purpose: a wrong
    // default would give a released track a second code.
    isrc_mode: '',
    // Same question for the single the track becomes; same reasoning.
    upc_mode: '',
    upc: '',
    version: 'original',
    explicit: '0',
    content_category: 'music',
    user_id: '',
};

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';
const muiField = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        '& fieldset': { borderColor: '#D1D5DB' },
        '&:hover fieldset': { borderColor: '#F59E0B' },
        '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
    },
};

const formatDuration = (ms) => {
    if (!ms) return '';
    const total = Math.round(ms / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

function Field({ label, error, hint, wide, children }) {
    return (
        <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
            {error
                ? <span className="mt-1 block text-xs text-red-600">{error}</span>
                : hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
        </label>
    );
}

export default function Upload() {
    const distributorId = localStorage.getItem('id');

    // /upload?artist=X arrives with the artist already chosen. Read once; the URL
    // does not change here.
    const [preset] = useState(() => {
        const artist = parseInt(new URLSearchParams(window.location.search).get('artist'), 10);
        return { artist: artist > 0 ? artist : null };
    });
    const [initialValues] = useState(() => ({
        ...INITIAL_VALUES,
        user_id: preset.artist ? String(preset.artist) : '',
    }));

    const [image, setImage] = useState(null);         // { file, url }
    const [song, setSong] = useState(null);           // { file, url }
    const [duration, setDuration] = useState(0);
    const [fileErrors, setFileErrors] = useState({});
    const [artists, setArtists] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [progress, setProgress] = useState(null);   // null | 0..100
    const [uploaded, setUploaded] = useState(null);   // { title } after success


    useEffect(() => {
        izenuAPI.get('/distributor/artist/fetchArtists.php?distributor_id=' + distributorId)
            .then(({ data }) => {
                const list = data.artists || [];
                setArtists(list);
                const artist = preset.artist && list.find(a => Number(a.user_id) === preset.artist);
                if (artist) setSelectedArtist(artist);
            })
            .catch(err => console.log(err));
        // Runs once: the preset comes from the URL at mount and never changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [distributorId]);

    useEffect(() => () => { if (image?.url) URL.revokeObjectURL(image.url); }, [image]);
    useEffect(() => () => { if (song?.url) URL.revokeObjectURL(song.url); }, [song]);


    const handleMusicChange = useCallback((event) => {
        const file = event.currentTarget.files[0];
        event.currentTarget.value = '';
        setFileErrors(prev => ({ ...prev, song: undefined }));
        if (!file) return;
        const problem = audioFileProblem(file);
        if (problem) {
            setFileErrors(prev => ({ ...prev, song: problem }));
            return;
        }
        const url = URL.createObjectURL(file);
        const audio = new Audio(url);
        setDuration(0);
        audio.onloadedmetadata = () => setDuration(Math.round(audio.duration * 1000));
        setSong({ file, url });
    }, []);

    const handleCoverChange = useCallback((event) => {
        const file = event.currentTarget.files[0];
        event.currentTarget.value = '';
        setFileErrors(prev => ({ ...prev, image: undefined }));
        if (!file) return;
        if (!['png', 'jpg', 'jpeg'].includes((file.name.split('.').pop() || '').toLowerCase())) {
            setFileErrors(prev => ({ ...prev, image: 'Formato não suportado. Usa JPG ou PNG.' }));
            return;
        }
        if (file.size > COVER_MAX_MB * 1024 * 1024) {
            setFileErrors(prev => ({ ...prev, image: `A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${COVER_MAX_MB} MB.` }));
            return;
        }
        setImage({ file, url: URL.createObjectURL(file) });
    }, []);

    // Returns true on success so the form only resets then; a failed upload
    // used to wipe everything the user had filled in.
    const uploadUnified = async (data) => {
        const formData = new FormData();
        ['user_id', 'title', 'genre', 'tags', 'country', 'link', 'version', 'explicit', 'content_category']
            .forEach(k => formData.append(k, data[k]));
        appendRights(formData, data.rights);
        formData.append('credits', JSON.stringify(data.credits || []));
        formData.append('isrc', data.isrc_mode === 'existing' ? data.isrc.trim().toUpperCase() : '');
        formData.append('isrc_pending', data.isrc_mode === 'izenu' ? '1' : '0');
        const upc = upcPayload(data.upc_mode, data.upc);
        formData.append('upc', upc.upc || '');
        formData.append('upc_pending', upc.upc_pending ? '1' : '0');
        formData.append('duration', duration || '');
        formData.append('cover_file', image.file);

        setProgress(0);
        try {
            // The audio first, straight to S3; kept so a retry does not send it again.
            let musicFile = song.uploaded;
            if (!musicFile) {
                musicFile = await uploadTrackAudio(song.file, { artistId: data.user_id, onProgress: setProgress });
                song.uploaded = musicFile;
            }
            formData.append('music_file', musicFile);
            // No manual Content-Type: axios has to set the multipart boundary itself.
            const { data: r } = await izenuAPI.post('/distributor/artist/insertMusic.php', formData);
            if (r.response) return true;
            alert('Erro ao carregar música: ' + (r.message || 'tenta mais tarde.'));
        } catch (error) {
            console.error('Error uploading', error);
            alert(error?.response?.data?.message || error?.message || 'Erro ao carregar música. Verifica a ligação e tenta outra vez.');
        } finally {
            setProgress(null);
        }
        return false;
    };

    const validateFiles = () => {
        const errs = {};
        if (!song) errs.song = 'Escolhe o ficheiro da música.';
        if (!image) errs.image = 'Escolhe a capa da música.';
        setFileErrors(prev => ({ ...prev, ...errs }));
        return Object.keys(errs).length === 0;
    };

    const uploading = progress !== null;

    if (uploaded) {
        return (
            <div className="mx-auto max-w-2xl p-3 sm:p-6 text-left">
                <section className="rounded-xl border border-green-200 bg-white p-6 text-center">
                    <MdCheckCircle size={48} className="mx-auto text-green-600" />
                    <h1 className="mt-2 text-xl font-bold text-gray-900">"{uploaded.title}" carregada</h1>
                    <p className="mt-1 text-sm text-gray-600">
                        Ficou como single. Podes juntá-la a um álbum ou EP em Carregar Álbum / EP ou em Agrupar singles num álbum.
                    </p>
                    <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                        <button type="button" onClick={() => setUploaded(null)}
                            className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600">Carregar outra música</button>
                        <Link to="/merge-singles" className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Agrupar singles num álbum</Link>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-5xl p-3 sm:p-6 text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Carregar música</h1>
            <p className="mb-5 text-sm text-gray-500">A música é publicada já aprovada, na conta do artista que escolheres.</p>

            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object({
                    title: Yup.string().trim().max(100, 'No máximo 100 caracteres.').required('Escreve o título da música.'),
                    user_id: Yup.string().required('Escolhe o artista.'),
                    genre: Yup.string().required('Escolhe o género.'),
                    isrc_mode: Yup.string().required('Diz se já tens o código ISRC desta música.'),
                    isrc: Yup.string().when('isrc_mode', {
                        is: 'existing',
                        then: (schema) => schema.trim()
                            .required('Insere o ISRC que a música já tem.')
                            .matches(/^[A-Za-z]{2}-?[A-Za-z0-9]{3}-?[0-9]{2}-?[0-9]{5}$/, 'Formato: CC-XXX-YY-NNNNN (ex.: AO-Z03-24-00001).'),
                        otherwise: (schema) => schema,
                    }),
                    upc_mode: Yup.string().test('upc', (v, ctx) => {
                        const problem = upcProblem(v, ctx.parent.upc);
                        return problem ? ctx.createError({ message: problem }) : true;
                    }),
                })}
                onSubmit={async (values, { resetForm }) => {
                    if (!validateFiles()) return;
                    const ok = await uploadUnified(values);
                    if (!ok) return;
                    setUploaded({ title: values.title });
                    // The artist stays chosen for the next single; only the track resets.
                    resetForm({ values: { ...INITIAL_VALUES, user_id: values.user_id } });
                    setImage(null);
                    setSong(null);
                    setDuration(0);
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, submitCount }) => {
                    const err = (k) => (touched[k] || submitCount > 0) ? errors[k] : undefined;
                    return (
                        <form onSubmit={(e) => { e.preventDefault(); validateFiles(); handleSubmit(); }} className="space-y-6">

                            {/* 1. Files */}
                            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                                <h2 className="mb-4 text-lg font-semibold text-gray-900">1. Ficheiros</h2>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div className="sm:col-span-2">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Música *</span>
                                        <label className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed p-4 hover:border-amber-400 ${fileErrors.song ? 'border-red-300 bg-red-50/40' : 'border-gray-300 bg-gray-50'}`}>
                                            <BsFileEarmarkMusic size={32} className="shrink-0 text-gray-400" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-800">{song ? song.file.name : 'Escolher ficheiro de áudio'}</span>
                                                <span className="block text-xs text-gray-500">{song ? `${(song.file.size / 1024 / 1024).toFixed(1)} MB${duration ? ` · ${formatDuration(duration)}` : ''}` : AUDIO_FORMATS_HINT}</span>
                                                {!song && <span className="block text-xs text-gray-400">{AUDIO_QUALITY_HINT}</span>}
                                            </span>
                                            <input type="file" accept={AUDIO_ACCEPT} onChange={handleMusicChange} className="sr-only" />
                                        </label>
                                        {song && <audio controls src={song.url} className="mt-2 w-full" />}
                                        {song && <SongSpecCheck file={song.file} durationMs={duration} />}
                                        {fileErrors.song && <p className="mt-1 text-xs text-red-600">{fileErrors.song}</p>}
                                    </div>
                                    <div>
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Capa *</span>
                                        <label className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed p-3 hover:border-amber-400 ${fileErrors.image ? 'border-red-300 bg-red-50/40' : 'border-gray-300 bg-gray-50'}`}>
                                            {image
                                                ? <img src={image.url} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
                                                : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-gray-200"><BsFileEarmarkImage size={22} className="text-gray-400" /></span>}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-800">{image ? image.file.name : 'Escolher capa'}</span>
                                                <span className="block text-xs text-gray-500">JPG/PNG · quadrada, {COVER_IDEAL_PX}×{COVER_IDEAL_PX} px · até {COVER_MAX_MB} MB</span>
                                            </span>
                                            <input type="file" accept="image/png,image/jpeg" onChange={handleCoverChange} className="sr-only" />
                                        </label>
                                        {image && <CoverSpecCheck file={image.file} />}
                                        {fileErrors.image && <p className="mt-1 text-xs text-red-600">{fileErrors.image}</p>}
                                    </div>
                                </div>
                            </section>

                            {/* 2. Artist */}
                            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                                <h2 className="mb-4 text-lg font-semibold text-gray-900">2. Artista</h2>
                                <div className="space-y-4">
                                    <div>
                                        <Autocomplete
                                            options={artists}
                                            value={selectedArtist}
                                            getOptionLabel={(o) => o.artist_name || o.name || ''}
                                            isOptionEqualToValue={(o, v) => o?.user_id === v?.user_id}
                                            onChange={(event, newValue) => {
                                                setSelectedArtist(newValue);
                                                setFieldValue('user_id', newValue ? newValue.user_id : '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Artista *" size="small" error={Boolean(err('user_id'))} helperText={err('user_id')} sx={muiField} />
                                            )}
                                        />
                                    </div>

                                    <p className="flex items-start gap-2 text-sm text-gray-600">
                                        <MdInfoOutline size={18} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
                                        <span>A música fica num <strong>single</strong> próprio. Para a pôr num álbum ou EP, usa <Link to="/releases" className="font-medium text-amber-700 underline">Carregar Álbum / EP</Link>.</span>
                                    </p>
                                </div>
                            </section>

                            {/* 3. Details */}
                            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                                <h2 className="mb-4 text-lg font-semibold text-gray-900">3. Detalhes da música</h2>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <Field label="Título *" error={err('title')} wide>
                                        <input name="title" className={inputCls} value={values.title} onChange={handleChange} onBlur={handleBlur} placeholder="Título da música" />
                                    </Field>
                                    <Field label="Género *" error={err('genre')}>
                                        <select name="genre" className={inputCls} value={values.genre} onChange={handleChange} onBlur={handleBlur}>
                                            <option value="">Escolhe um género</option>
                                            {GENRES.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="País">
                                        <select name="country" className={inputCls} value={values.country} onChange={handleChange} onBlur={handleBlur}>
                                            {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Versão">
                                        <select name="version" className={inputCls} value={values.version} onChange={handleChange}>
                                            <option value="original">Original</option>
                                            <option value="remix">Remix</option>
                                            <option value="cover">Cover</option>
                                            <option value="live">Ao vivo</option>
                                        </select>
                                    </Field>
                                    <Field label="Conteúdo explícito">
                                        <select name="explicit" className={inputCls} value={values.explicit} onChange={handleChange}>
                                            <option value="0">Não</option>
                                            <option value="1">Sim</option>
                                        </select>
                                    </Field>
                                    <div className="sm:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                            <span className="text-sm font-medium text-gray-700">Tens o código ISRC desta música? <span className="text-red-500">*</span></span>
                                            <div className={`inline-flex overflow-hidden rounded-full !border !border-solid ${err('isrc_mode') && !values.isrc_mode ? 'border-red-400' : 'border-gray-300'}`} role="radiogroup">
                                                {[['existing', 'Sim'], ['izenu', 'Não']].map(([value, label]) => (
                                                    <button key={value} type="button" role="radio" aria-checked={values.isrc_mode === value}
                                                        onClick={() => setFieldValue('isrc_mode', value)}
                                                        className={`px-4 py-1 text-xs font-medium ${values.isrc_mode === value ? 'bg-amber-500 !text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {values.isrc_mode === 'existing' && (
                                            <>
                                                <input name="isrc" aria-label="ISRC" className={`${inputCls} mt-2 !w-56 ${err('isrc') ? '!border-red-400' : ''}`}
                                                    value={values.isrc} onChange={handleChange} onBlur={handleBlur} placeholder="AO-Z03-24-00001" />
                                                {err('isrc')
                                                    ? <p className="mt-1 text-xs text-red-600">{err('isrc')}</p>
                                                    : <p className="mt-1 text-xs text-gray-500">Usa o mesmo código das outras plataformas: um segundo dividia as reproduções e os royalties.</p>}
                                            </>
                                        )}
                                        {values.isrc_mode === 'izenu' && <p className="mt-1 text-xs text-gray-500">Sem problema: a Izenu gera um ISRC para esta faixa.</p>}
                                        {!values.isrc_mode && (err('isrc_mode')
                                            ? <p className="mt-1 text-xs text-red-600">{err('isrc_mode')}</p>
                                            : <p className="mt-1 text-xs text-gray-500">O ISRC identifica a gravação em todas as plataformas e é por ele que os royalties chegam ao artista.</p>)}
                                    </div>
                                    <div className="sm:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <CreditsInputs value={values.credits}
                                            mainArtist={selectedArtist?.artist_name || selectedArtist?.name}
                                            onChange={(credits) => setFieldValue('credits', credits)} />
                                    </div>
                                    <div className="sm:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <UpcQuestion mode={values.upc_mode} upc={values.upc}
                                            onModeChange={(m) => setFieldValue('upc_mode', m)} onUpcChange={(v) => setFieldValue('upc', v)}
                                            showError={Boolean(err('upc_mode'))} />
                                    </div>
                                    <Field label="Categoria">
                                        <select name="content_category" className={inputCls} value={values.content_category} onChange={handleChange}>
                                            <option value="music">Música</option>
                                            <option value="noise">Ruído / Noise</option>
                                        </select>
                                    </Field>
                                    <Field label="Tags" hint="Opcional. Palavras que descrevem a música, separadas por vírgulas.">
                                        <input name="tags" className={inputCls} value={values.tags} onChange={handleChange} placeholder="kizomba, romântica" />
                                    </Field>
                                    <Field label="Link" hint="Opcional. Por exemplo, o vídeo no YouTube.">
                                        <input name="link" type="url" className={inputCls} value={values.link} onChange={handleChange} placeholder="https://…" />
                                    </Field>
                                    <div className="sm:col-span-2">
                                        <RightsInputs value={values.rights}
                                            holderPlaceholder={selectedArtist?.artist_name || selectedArtist?.name}
                                            labelPlaceholder={localStorage.getItem('name') || ''}
                                            onChange={(p) => setFieldValue('rights', { ...values.rights, ...p })} />
                                    </div>
                                </div>
                            </section>

                            {/* Submit */}
                            <div className="sticky bottom-0 -mx-3 border-t bg-gray-50/95 px-3 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0">
                                {uploading && (
                                    <div className="mb-2">
                                        <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                                            <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-600">{progress < 100 ? `A enviar… ${progress}%` : 'A processar no servidor…'} Não feches esta página.</p>
                                    </div>
                                )}
                                <button type="submit" disabled={uploading}
                                    className="w-full rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-60 sm:w-auto">
                                    {uploading ? 'A carregar…' : 'Carregar música'}
                                </button>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
}

// Checks the chosen MP3 against the recommendation. The bitrate is estimated from
// size and duration, which is close enough for constant-bitrate MP3s.
function SongSpecCheck({ file, durationMs }) {
    if (!durationMs) return null;
    if (['wav', 'wave', 'flac'].includes(audioExt(file.name))) {
        return <p className="mt-1 text-xs"><Check ok>Sem perdas (WAV/FLAC) — convertido para MP3 320 kbps para tocar</Check></p>;
    }
    const kbps = Math.round((file.size * 8) / (durationMs / 1000) / 1000);
    return (
        <p className="mt-1 text-xs">
            <Check ok={kbps >= SONG_IDEAL_KBPS - 10}>
                ~{kbps} kbps{kbps < SONG_IDEAL_KBPS - 10 ? ` — abaixo dos ${SONG_IDEAL_KBPS} kbps recomendados` : ''}
            </Check>
        </p>
    );
}

// Checks the chosen cover: it must be square, ideally COVER_IDEAL_PX on each side.
function CoverSpecCheck({ file }) {
    const dims = useImageSize(file);
    if (!dims) return null;
    const square = dims.w === dims.h;
    const bigEnough = Math.min(dims.w, dims.h) >= COVER_IDEAL_PX;
    return (
        <p className="mt-1 flex flex-wrap gap-x-3 text-xs">
            <Check ok={square}>{dims.w}×{dims.h} px{square ? '' : ' — não é quadrada, vai ser cortada'}</Check>
            {!bigEnough && <Check ok={false}>abaixo de {COVER_IDEAL_PX}×{COVER_IDEAL_PX}</Check>}
        </p>
    );
}
